import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FOLLOW_KEY = "legacy.followedTeams.v1";
const NOTIFY_KEY = "legacy.kickoffAlerts.v1"; // stores fixture ids we've already notified
const ALERT_WINDOW_MS = 5 * 60 * 1000; // 5 minutes before kickoff
const POLL_MS = 60 * 1000;

function readSet(key) {
  try {
    const raw = localStorage.getItem(key);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}

export default function KickoffAlerts() {
  const { user } = useAuth();
  const isAuthed = user && user !== false && typeof user === "object";
  const timerRef = useRef(null);

  useEffect(() => {
    if (!isAuthed) return;
    if (typeof window === "undefined" || !("Notification" in window)) return;

    // Try to get permission once (silent no-op if already denied/granted)
    if (Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }

    const notified = readSet(NOTIFY_KEY);

    const tick = async () => {
      if (Notification.permission !== "granted") return;
      let followed = readSet(FOLLOW_KEY);
      // Auto-include owner/rep teams
      try {
        const { data } = await api.get("/me/teams");
        for (const t of data || []) followed.add(t.id);
      } catch { /* ignore */ }
      if (followed.size === 0) return;

      let fixtures = [];
      try {
        const { data } = await api.get("/fixtures?status=upcoming");
        fixtures = data || [];
      } catch { return; }

      const now = Date.now();
      for (const f of fixtures) {
        if (notified.has(f.id)) continue;
        const kickoff = new Date(f.scheduled_at).getTime();
        const delta = kickoff - now;
        if (delta > 0 && delta <= ALERT_WINDOW_MS) {
          const involves = followed.has(f.home_team_id) || followed.has(f.away_team_id);
          if (!involves) continue;
          const mins = Math.max(1, Math.round(delta / 60000));
          try {
            const n = new Notification("Kickoff in " + mins + " min · Legacy", {
              body: (f.map ? `${f.map} · ` : "") + `Match starts at ${new Date(f.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
              tag: `legacy-kickoff-${f.id}`,
              icon: "/favicon.ico",
            });
            n.onclick = () => {
              window.focus();
              window.location.href = "/fixtures";
            };
            notified.add(f.id);
            try { localStorage.setItem(NOTIFY_KEY, JSON.stringify([...notified])); } catch {}
          } catch { /* ignore */ }
        }
      }
    };

    tick();
    timerRef.current = setInterval(tick, POLL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isAuthed]);

  return null;
}

// Helpers so other pages can add/remove followed team ids and stay in sync.
export function useFollowedTeams() {
  const [set, setSet] = useState(() => readSet(FOLLOW_KEY));
  const toggle = (teamId) => {
    setSet((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) next.delete(teamId); else next.add(teamId);
      try { localStorage.setItem(FOLLOW_KEY, JSON.stringify([...next])); } catch {}
      return next;
    });
  };
  return { followed: set, toggle };
}
